// Third-party
import React from 'react';

// Utils
import { formatLocalizedString, formatMessage } from '../../../../../utils/i18n';

// Components
import GroupInfoStep from './GroupInfoStep';
import { HoldingIcon } from '../../../common';
import { ContactInitialsLabel } from '../../../misc';
import ComparisonItem from '@/types/comparison-item';
import { Inline } from '@capintel/component-lib/inline';
import { Typography } from '@capintel/component-lib/typography';

type Props = {
  name: string;
  groupColor: string;
  selectedComparisonItems: ComparisonItem[];
  unselectedComparisonItems: ComparisonItem[];
  onNameChange: (name: string) => void;
  onAddItem: (item: ComparisonItem) => void;
  onRemoveItem: (item: ComparisonItem) => void;
  isEditing?: boolean;
};

const GroupContentStep = (props: Props) => {
  const { name, selectedComparisonItems, unselectedComparisonItems, onNameChange, onAddItem, onRemoveItem, groupColor, isEditing } = props;

  const emptyGroup = selectedComparisonItems.length === 0;

  return (
    <div className="group-content">
      <div className="grid">
        <span className="selection-title">
          <Typography as="h3" variant="labelMdSemibold">
            {formatMessage('AddComparisonGroupModal.GroupContentStep.Instructions')}
          </Typography>
        </span>
        {isEditing && (
          <Typography as="h3" variant="labelMdSemibold">
            {formatMessage('AddComparisonGroupModal.GroupContentStep.CurrentItems')}
          </Typography>
        )}
        {!isEditing && (
          <Inline gap="200">
            <div className="group-color" style={{ backgroundColor: groupColor }} />
            <GroupInfoStep compact name={name} onNameChange={onNameChange} editable />
          </Inline>
        )}
      </div>

      <hr />

      <div className="grid item-box">
        <ul className="item-column column-left">
          {unselectedComparisonItems.map((item, index) => {
            const itemContact = item.contactsInfo ? item.contactsInfo[0] : null;

            return (
              <li data-private data-dd-privacy="mask" key={`unselectedComparisonItems-${index}`}>
                <button className="item button-selection" type="button" onClick={() => onAddItem(item)}>
                  <div className="d-flex flex-row align-items-center">
                    <HoldingIcon type={item.type} color={item.color} />
                    <span>{formatLocalizedString(item.name)}</span>
                  </div>
                  {itemContact && <ContactInitialsLabel contact={itemContact} />}
                  <i className="icon-plus-med" />
                </button>
              </li>
            );
          })}
        </ul>

        <ul className={`item-column column-right ${emptyGroup ? 'no-items-added' : ''}`}>
          {selectedComparisonItems.map((item, index) => {
            const itemContact = item.contactsInfo ? item.contactsInfo[0] : null;
            return (
              <li data-private data-dd-privacy="mask" key={`selectedComparisonItems-${index}`} className="item">
                <div className="d-flex flex-row align-items-center">
                  <HoldingIcon type={item.type} color={item.color} />
                  <span>{formatLocalizedString(item.name)}</span>
                </div>
                {itemContact && <ContactInitialsLabel contact={itemContact} />}
                <button type="button" className="button-selection" onClick={() => onRemoveItem(item)}>
                  <i className="icon-ds-delete" />
                </button>
              </li>
            );
          })}

          {emptyGroup && (
            <li className="d-flex flex-column align-items-center">
              <i className="icon-empty-drawer" />
              <span>{formatMessage('AddComparisonGroupModal.GroupContentStep.NoItem')}</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default GroupContentStep;
